<template>
    <li class="h-100">
        <div v-click-outside="hideNav"
             class="menu-button h-100 d-flex align-items-center"
             :class="[getButtonStyling()]"
             @click="navClick">
            <span class="menu-item d-flex align-items-center position-relative">
                <component :is="getIconByName(img)" v-if="img" class="menu-logo">
                </component>
                <span v-if="count" id="count" class="text-white position-absolute">
                    {{ count.toLocaleString('nl-NL', {
                        minimumIntegerDigits: 2,
                        useGrouping: false,
                    }) }}
                </span>
                <span class="menu-link" :class="active ? 'red' : ''">
                    {{ label }}
                    <component :is="show ? ChevronUp : ChevronDown" v-if="menu" class="chevron">
                    </component>
                </span>
            </span>

            <teleport to="body">
                <div v-if="menu && show"
                     class="menu-dropdown-container"
                     :class="{
                         'scrolled': isScrolled
                     }">
                    <ul class="list-unstyled menu-dropdown big d-flex flex-wrap justify-content-start"
                        :class="{
                            'scrolled': isScrolled,
                            'small': menuType === 'small'
                        }">
                        <li v-for="(menuItem, index) in menu"
                            :key="index"
                            class="d-flex menu-dropdown-item"
                            @click="menuItemClicked(menuItem)">
                            <component :is="getIconByName((menuItem as any)?.img)"
                                       v-if="(menuItem as any)?.img && getIconByName((menuItem as any)?.img)"
                                       class="menu-item-logo"
                                       :alt="(menuItem as any)?.img" />
                            <span class="menu-dropdown-link">
                                {{ (menuItem as any)?.label }}
                            </span>
                        </li>
                    </ul>
                </div>
            </teleport>
        </div>
    </li>
</template>

<script lang="ts">
import {
    ref, computed, onMounted, onUnmounted, 
} from 'vue';
import AccountIcon from '@/assets/icons/account.svg';
import ChevronDown from '@/assets/icons/chevron-down.svg';
import ChevronUp from '@/assets/icons/chevron-up.svg';
import HeartIcon from '@/assets/icons/heart-outline.svg';
import InvoicesIcon from '@/assets/icons/invoices.svg';
import LogoutIcon from '@/assets/icons/logout.svg';
import OrderedBeforeIcon from '@/assets/icons/ordered-before.svg';
import OrdersIcon from '@/assets/icons/orders.svg';
import ShoppingCartIcon from '@/assets/icons/winkelmand_empty.svg';
import ShoppingCartFilledIcon from '@/assets/icons/winkelmand_filled.svg';
import { useUIStore } from '@/stores/ui';

export default {
    props: {
        label: {
            type: String,
        },
        img: {
            type: String,
        },
        menu: {
            type: Array,
        },
        menuType: {
            type: String,
            default: 'small',
        },
        styling: {
            type: String,
        },
        design: {
            type: String,
        },
        count: {
            type: Number,
            required: false,
        },
        active: {
            type: Boolean,
            default: false,
        },
        routeName: {
            type: String,
            default: '',
        },
    },
    setup(props: any, context: any) {
        const uiStore = useUIStore();

        const icons = [
            {
                name: 'account',
                component: AccountIcon,
            },
            {
                name: 'winkelmand_empty',
                component: ShoppingCartIcon,
            },
            {
                name: 'winkelmand_filled',
                component: ShoppingCartFilledIcon,
            },
            {
                name: 'heart-outline',
                component: HeartIcon,
            },
            {
                name: 'ordered-before',
                component: OrderedBeforeIcon,
            },
            {
                name: 'invoices',
                component: InvoicesIcon,
            },
            {
                name: 'logout',
                component: LogoutIcon,
            },
            {
                name: 'orders',
                component: OrdersIcon,
            },
        ];

        function getIconByName(name: string) {
            return icons.find((icon: any) => icon.name === name)?.component;
        }

        function menuItemClicked(menuItem: any) {
            context.emit('menuItemClicked', menuItem);
        }

        function getButtonStyling() {
            const styleObj: any = {};

            if (props.design) {
                styleObj[props.design] = true;
            }

            return styleObj;
        }

        const show = ref(false);
        const isScrolled = computed(() => uiStore.getIsScrolled);

        onMounted(() => {
            uiStore.initScrollListener();
        });

        onUnmounted(() => {
            uiStore.removeScrollListener();
        });

        function navClick() {
            show.value = !show.value;
            context.emit('navClick');
        }

        function hideNav() {
            show.value = false;
        }

        return {
            navClick,
            hideNav,
            show,
            isScrolled,
            menuItemClicked,
            getButtonStyling,
            getIconByName,
            ChevronDown,
            ChevronUp,
        };
    },
};
</script>

<style scoped lang="scss">
@import "~@/assets/variables";

#count {
    top: 12px;
    left: 6.5px;
    font-size: .6rem;
    font-family: Avenir, sans-serif;
}

li {
    display: inline;
    float: left;
    height: 100%;

    .menu-button {
        margin: 0 10px;

        &.relative {
            position: relative;
        }

        .menu-item {
            font-weight: 500;
            font-family: "DINPro", serif;
            font-stretch: semi-condensed;
            letter-spacing: 1.13px;
            font-size: 18px;
            text-transform: uppercase;
            height: 30px;

            &:hover {
                cursor: pointer;

                .menu-link {
                    border-bottom: 1px solid $kwast-red;
                }
            }

            .menu-logo {
                color: $black;
                margin-right: 8px;
                height: 22px;
                width: 22px;
            }

            .menu-link {
                line-height: 29px;
                margin-right: 8px;
                border-bottom: 1px solid transparent;

                .chevron {
                    width: 12px;
                    height: 20px;
                    margin-bottom: 4px;
                    font-weight: 200;
                }
            }
        }

        &.hamburger {
            .menu-item {
                border-top: 1px solid $black;
                border-bottom: 1px solid $black;

                .menu-link {
                    margin-right: 0;
                }
            }
        }
    }

}

.menu-dropdown-container {
    position: fixed;
    background: $white;
    top: 100px;
    left: 0;
    right: 0;
    width: 100%;
    box-shadow: 0px 0px 10px 0px #D9D9D9;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: top 0.3s ease;

    &.scrolled {
        top: 70px;
    }
}

.menu-dropdown {
    background: $white;
    max-width: 880px;
    padding: 11px 0;
    transform: translateY(100%);
    bottom: 0;
    left: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 200px;

    li {
        padding: 11px 2rem;
        flex-grow: 1;
        font-family: "DINPro", serif;
        font-weight: 300;
        font-stretch: condensed;
        letter-spacing: .4px;
        font-size: 1rem;
    }

    .menu-dropdown-item {
        cursor: pointer;
        text-wrap: none;

        &:hover {
            .menu-dropdown-link {
                border-bottom: 1px solid $kwast-red;
            }
        }

        .menu-item-logo {
            margin-right: 10px;
            flex: none;
            font-size: 1rem;
            width: 22px;
            height: 22px;
        }

        .menu-dropdown-link {
            flex: 0 0 auto;
            font-size: 1rem;
            flex-grow: 1;
            border-bottom: 1px solid transparent;
        }
    }

    &.big {
        // position: fixed !important;
        top: 100px;
        bottom: auto;
        left: 0;
        width: 100%;
        max-height: 287px;
        padding: 60px 0 80px 0;
        box-shadow: none;
        transform: none;
        z-index: 500;

        &.scrolled {
            top: 70px;
        }

        &.small {
            max-height: 140px;
        }

        li {
            font-family: "DINPro-regular", serif;
            height: 20px;
            padding: 0 1rem 0 0;
            flex-grow: 0;
            flex-shrink: 0;
            flex-basis: 25%;
            width: 25%;
            box-sizing: border-box;
            margin-bottom: 7px;
            font-weight: 300;
            font-stretch: condensed;
            font-size: 1rem;

            &:nth-last-child(2) {
                margin-top: 1px;
            }
        }

        .menu-dropdown-item {
            cursor: pointer;
            overflow-wrap: normal;

            &:hover {
                /*
                @font-face {
                    font-family: "DINPro";
                    font-weight: 500;
                    font-style: normal;
                    src: url("~@/assets/fonts/din-pro/DINPro-Medium.woff");
                }
    */
                .menu-dropdown-link {
                    font-family: "DINPro", serif;
                    font-weight: 500;
                    font-stretch: normal;
                    letter-spacing: .4px;
                    font-size: 1rem;
                    border-bottom: 1px solid black;
                }
            }

            .menu-dropdown-link {
                text-align: left;
                font-size: 1rem;
                line-height: 1.25;
                letter-spacing: .4px;
                text-wrap: none;
                flex-grow: 0;
                border-bottom: 1px solid transparent;
            }
        }
    }
}

@media only screen and (max-width: 1200px) {
    li .menu-button .menu-item .menu-logo {
        margin: 0;
    }
}
</style>
